/**
 * Metabase API Service
 * Flat resources + dashboard details from REST backend.
 */

import { apiClient } from '@/services/core/apiClient';
import { logger } from '@/config/logger';
import type { MetabaseFlatResource, MetabaseDashboardDetails } from '@/types/dashboardConfig';

/**
 * Extract an array from a possibly-wrapped API payload.
 * Handles plain arrays and envelopes like { data: [] }, { items: [] }, { resources: [] }.
 */
function extractArray<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    for (const key of ['data', 'items', 'resources', 'dashboardItems']) {
      if (Array.isArray(record[key])) return record[key] as T[];
    }
  }
  return [];
}

/**
 * GET /api/ngpep/{loginUserId}/metabase/resources/flat
 * Returns all Metabase resources; caller should filter by resourceType as needed.
 */
export async function getResourcesFlat(
  loginUserId: string | number,
  token: string
): Promise<MetabaseFlatResource[]> {
  const endpoint = `/api/ngpep/${loginUserId}/metabase/resources/flat`;
  const response = await apiClient.get<unknown>(endpoint, token);

  if (!response.success) {
    throw new Error(response.error ?? 'getResourcesFlat: Failed to fetch resources');
  }

  const items = extractArray<MetabaseFlatResource>(response.data);

  if (items.length === 0) {
    logger.warn('[metabaseApi] getResourcesFlat: received 0 items. Raw response.data:', response.data);
  } else {
    logger.log(`[metabaseApi] getResourcesFlat: loaded ${items.length} resource(s)`);
  }

  // Ensure compositeKey is always set — compute it client-side if the backend omits it
  return items.map((item) => ({
    ...item,
    compositeKey: item.compositeKey ?? `${item.resourceType}:${item.resourceId}`,
  }));
}

/**
 * GET /api/ngpep/{loginUserId}/metabase/dashboard/{dashboardId}/details
 * Returns questions + filters for a dashboard. Supports AbortSignal for race cancellation.
 */
export async function getDashboardDetails(
  loginUserId: string | number,
  dashboardId: number,
  token: string,
  signal?: AbortSignal
): Promise<MetabaseDashboardDetails> {
  const endpoint = `/api/ngpep/${loginUserId}/metabase/dashboard/${dashboardId}/details`;

  const response = await apiClient.request<MetabaseDashboardDetails>(endpoint, {
    method: 'GET',
    token,
  });

  if (signal?.aborted) {
    throw new DOMException('getDashboardDetails: Request aborted', 'AbortError');
  }

  if (!response.success) {
    throw new Error(response.error ?? 'getDashboardDetails: Failed to fetch dashboard details');
  }

  return response.data as MetabaseDashboardDetails;
}
